import type {
  StageConfig,
  HabitatType,
} from "@/phaser/core/monsters/monsterCatalog";
import type { Direction } from "@phaser/core/queueItem/queueItem";
import { ScoreManager } from "@/phaser/core/game/ScoreManager";
import type { CountdownTimer } from "./CountdownTimer";

type GameRuleConfig = {
  stage: StageConfig;
  scoreManager: ScoreManager;
  timer: CountdownTimer;

  // 옵션: 시간/점수 규칙 튜닝용
  correctScore?: number; // 기본 +점
  wrongScore?: number; // 기본 -점
  correctTimeBonusMs?: number; // 정답 시 추가 시간
  wrongTimePenaltyMs?: number; // 오답 시 감소 시간
};

export class GameRuleManager {
  private stage: StageConfig;
  private scoreManager: ScoreManager;
  private timer: CountdownTimer;

  private correctScore: number;
  private wrongScore: number;
  private correctTimeBonusMs: number;
  private wrongTimePenaltyMs: number;

  // 연속 정답 횟수
  private combo = 0;

  constructor(config: GameRuleConfig) {
    this.stage = config.stage;
    this.scoreManager = config.scoreManager;
    this.timer = config.timer;

    this.correctScore = config.correctScore ?? 1; // 정답 시 +1
    this.wrongScore = config.wrongScore ?? 0; // 기본: 오답 점수 패널티 없음
    this.correctTimeBonusMs = config.correctTimeBonusMs ?? 2000;
    this.wrongTimePenaltyMs = config.wrongTimePenaltyMs ?? -1000;
  }

  handleResolved(habitat: HabitatType, direction: Direction) {
    const isCorrect = this.isCorrectHabitat(habitat, direction);

    const comboBonus = isCorrect ? this.applyCorrect() : 0;

    if (!isCorrect) {
      this.applyWrong();
    }

    return {
      isCorrect,
      combo: this.combo,
      comboBonus,
    };
  }

  private isCorrectHabitat(
    habitat: HabitatType,
    direction: Direction,
  ): boolean {
    return habitat === this.getSelectedHabitat(direction);
  }

  private getSelectedHabitat(direction: Direction): HabitatType {
    return direction === "LEFT"
      ? this.stage.leftHabitat
      : this.stage.rightHabitat;
  }

  private applyCorrect(): number {
    this.combo += 1;

    this.scoreManager.add(this.correctScore);

    let comboBonus = 0;

    if (this.combo % 10 === 0) {
      comboBonus = Math.min(this.combo, 100);
      this.scoreManager.add(comboBonus);
    }

    this.timer.addTime(this.correctTimeBonusMs);

    return comboBonus;
  }

  private applyWrong() {
    this.combo = 0;
    this.scoreManager.add(this.wrongScore);
    this.timer.addTime(this.wrongTimePenaltyMs);
  }

  reset() {
    this.combo = 0;
  }
}

// 코인획득 계산 함수
export function calcEarnedCoins(score: number): number {
  return score <= 100 ? 0 : Math.floor(score * 0.01);
}
